import { useState, useEffect } from 'react'
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@cutilei/ui'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { getApiErrorMessage, logApiError } from 'services/apiError'
import TimeWindow from 'services/timeWindow'
import Select from 'components/Inputs/Select'
import DateInput from 'components/Inputs/DateInput'
import MainButton from 'components/Buttons/MainButton'
import submitOnEnter from 'utils/submitOnEnter'
import holidayValidator from 'validators/holidayValidator'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { Formik } from 'formik'

import {
  Form,
  FormField,
  Input,
  Row,
  WhiteText,
  WarningText,
  ErrorContainer
} from './styles'

export default function EditHolidayModal ({
  visible,
  data: holiday,
  onConfirm: handleConfirm = () => {},
  onClose: handleClose = () => {}
}) {
  const [loading, setLoading] = useState (true)
  const [businessHours, setBusinessHours] = useState (null)
  const [startTimeWindows, setStartTimeWindows] = useState ([])
  const [endTimeWindows, setEndTimeWindows] = useState ([])
  const [errorMessage, setErrorMessage] = useState (null)
  const businessId = AuthService.getBusinessId ()
  const token = AuthService.getToken ()

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  useEffect (() => {
    if (visible) getData ()
  }, [visible])

  const getData = async () => {
    try {
      const { data: workingDays } = await cutileiApi.get (`/businesses/${businessId}/working_days`)

      const businessHours = TimeWindow.getBusinessHours(workingDays)

      setStartTimeWindows (
        TimeWindow.getTimeWindows (businessHours?.opening, businessHours?.closing, false)
      )
      
      setEndTimeWindows (
        TimeWindow.getTimeWindows (businessHours?.opening, businessHours?.closing, false)
      )

      setBusinessHours (businessHours)
    } catch (error) {
      setErrorMessage (getApiErrorMessage (error))
      logApiError (error)
    } finally {
      setLoading (false)
    }
  }

  return (
    <Dialog open={visible} onOpenChange={open => { if (!open) handleClose() }}>
      <DialogContent size='md' closeOnOutsideClick={false} aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Alterar atendimento no feriado</DialogTitle>
        </DialogHeader>
        {loading ? <DialogBody><Loading/></DialogBody> : (
          <Formik
            validationSchema={holidayValidator}
            initialValues={{
              'start_date': holiday.start_date,
              'end_date': holiday.end_date,
              'start_time': {
                label: holiday.start_time,
                value: holiday.start_time
              },
              'end_time': {
                label: holiday.end_time,
                value: holiday.end_time
              }
            }}
            onSubmit={async (values, { setSubmitting }) => {
              try {
                setErrorMessage (null)
              
                const holidayData = {
                  ...holiday,
                  ...values,
                  start_time: values.start_time.value,
                  end_time: values.end_time.value
                }

                await cutileiApi.put (
                  `/businesses/${businessId}/holidays/${holiday.id}`,
                  holidayData,
                  requestConfig
                )

                setSubmitting (false)
                handleConfirm (holidayData)
                handleClose ()
              } catch (error) {
                setSubmitting (false)
                setErrorMessage (getApiErrorMessage (error))
                logApiError (error)
              }
            }}
          >
            {({
              values,
              errors,
              touched,
              isSubmitting,
              setFieldValue,
              setFieldTouched,
              handleSubmit
            }) => {
              const handleTimeSelected = (name, option) => {
                if (name === 'start_time') {
                  setEndTimeWindows (TimeWindow.getTimeWindows (option.value, businessHours?.closing, true))
                } else if (name === 'end_time') {
                  setStartTimeWindows (TimeWindow.getTimeWindows (businessHours?.opening, option.value, true))
                }
                setFieldValue (name, option)
              }

              return (
                <>
                  <DialogBody>
                    <Form onSubmit={handleSubmit} onKeyDown={submitOnEnter(handleSubmit, isSubmitting)}>
                      <Input value={holiday.name} disabled={true}/>
                      <Row>
                        <FormField>
                          <DateInput
                            placeholder='Data de início'
                            value={values.start_date}
                            onChange={date => setFieldValue ('start_date', date)}
                            style={{width: '100%'}}
                          />
                          {touched.start_date && errors.start_date && (
                            <WarningText>
                              {errors.start_date}
                            </WarningText>
                          )}
                        </FormField>
                        <FormField>
                          <DateInput
                            placeholder='Data final'
                            value={values.end_date}
                            onChange={date => setFieldValue ('end_date', date)}
                            style={{width: '100%'}}
                          />
                          {touched.end_date && errors.end_date && (
                            <WarningText>
                              {errors.end_date}
                            </WarningText>
                          )}
                        </FormField>
                      </Row>
                      <Row>
                        <FormField>
                          <Select
                            name='start_time'
                            placeholder='Horário inicial...'
                            value={values.start_time}
                            options={startTimeWindows}
                            onChange={handleTimeSelected}
                            onBlur={setFieldTouched}
                            error={errors.start_time}
                            touched={touched.start_time}
                          />
                          {touched.start_time && errors.start_time && (
                            <WarningText>
                              {errors.start_time}
                            </WarningText>
                          )}
                        </FormField>
                        <FormField>
                          <Select
                            name='end_time'
                            placeholder='Horário final...'
                            value={values.end_time}
                            options={endTimeWindows}
                            onChange={handleTimeSelected}
                            onBlur={setFieldTouched}
                            error={errors.end_time}
                            touched={touched.end_time}
                          />
                          {touched.end_time && errors.end_time && (
                            <WarningText>
                              {errors.end_time}
                            </WarningText>
                          )}
                        </FormField>
                      </Row>
                      {errorMessage && (
                        <ErrorContainer>
                          <WhiteText>{errorMessage}</WhiteText>
                        </ErrorContainer>
                      )}
                    </Form>
                  </DialogBody>
                  <DialogFooter>
                    <MainButton
                      label='Salvar alterações'
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      loading={isSubmitting}
                      width={160}
                    />
                  </DialogFooter>
                </>
              )
            }}
          </Formik>
        )}
      </DialogContent>
    </Dialog>
  )
}
